import { useSelector } from 'react-redux';
import merge from 'lodash.merge';
import { patchOrgAttributes } from '@rsos/sinatra/src/actions/updateAttributes';
import { useCurrentOrg } from '@rsos/sinatra/src/selectors';
import { selectOrgAudioSettings } from '@rsos/sinatra/src/selectors/selectAudioSettings';
import { CapstoneAudioSettings } from '@rsos/sinatra/src/types/Organization';

/**
 * This patch update does a deep merge, so we need a deeply-partial type.
 */
type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

/**
 * Capstone has a bunch of different features with audio settings, each of which
 * has several different key/value pairs. This allows you to patch just the
 * key/value pair you're interested in, on the feature you're interested in,
 * without touching anything else.
 */
export function usePatchAudioSettings<T extends keyof CapstoneAudioSettings>(
  feature: T,
) {
  const orgId = useCurrentOrg()?.id;
  const currentSettings = useSelector(selectOrgAudioSettings('capstone'));
  return (update: DeepPartial<CapstoneAudioSettings[T]>) => {
    if (!orgId) {
      throw 'Cannot patch audio settings without a current organization';
    }
    return patchOrgAttributes(orgId, 'capstone', {
      audio_settings: merge({}, currentSettings, {
        [feature]: { ...currentSettings[feature], ...update },
      }),
    } as Partial<CapstoneAudioSettings>);
  };
}
